import { useCallback, useEffect, useRef, useState } from 'react'

export type AccessPhase = 'checking' | 'allowed' | 'denied' | 'error'

export interface AccessResponse {
  allowed: boolean
}

type Call = <T>(path: string) => Promise<T>

// The call is injected rather than resolved from context so the hook can be
// driven directly in a test without mounting an auth provider.
export function useAccess(call: Call) {
  const [phase, setPhase] = useState<AccessPhase>('checking')
  const [attempt, setAttempt] = useState(0)

  // `call` is not a dependency of the effect below. useApiFetch's returned
  // function is a useCallback keyed on live auth state, so its identity can
  // change between renders; if the effect re-ran on every such change it
  // would restart the in-flight request each time phase settles and
  // re-renders the component, which recomputes `call` again — a
  // checking/settled loop that never lets the phase stick. Keying the effect
  // on `attempt` alone, and always reading the latest `call` through this
  // ref, decouples "when to fetch" (mount + explicit retry) from "which
  // function instance to call" (always the newest one).
  const callRef = useRef(call)
  callRef.current = call

  useEffect(() => {
    let cancelled = false
    setPhase('checking')
    callRef.current<AccessResponse>('/access')
      .then((res) => {
        if (!cancelled) setPhase(res.allowed ? 'allowed' : 'denied')
      })
      .catch(() => {
        if (!cancelled) setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  return { phase, retry }
}
